import { Field, FieldArray, useFormikContext } from 'formik';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SquareFormValues } from '@/utils/forms/squareTransformers';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { InfoIconDialog } from '@/components/ui/info-icon-dialog';
import { Switch } from '@/components/ui/switch';
import { SquaresStickyFooterAd } from '@/components/squares/SquaresStickyFooterAd';
import { type GoogleAdsPlacement } from '@/api/tenants/googleAdsPlacements';

interface AdsSectionProps {
  disabled?: boolean;
  tenantId?: string;
}

interface AdUnit {
  type: string;
  sizes?: string[];
  gam_ad_unit_id?: string;
}

export function AdsSection({ disabled = false, tenantId }: AdsSectionProps) {
  const { values, setFieldValue } = useFormikContext<SquareFormValues>();

  const adUnits = (values.ad_units || []) as AdUnit[];

  const stickyFooterPlacement = values.sticky_footer_placement as
    | GoogleAdsPlacement
    | null
    | undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">Ad Units (GAM)</h3>
        <InfoIconDialog title="Ad Units (GAM)" ariaLabel="Ad units information">
          Configure Google Ad Manager (GAM) ad units for sponsored content.
        </InfoIconDialog>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="gam_enabled"
          checked={values.gam_enabled !== false}
          onCheckedChange={checked => setFieldValue('gam_enabled', checked)}
          disabled={disabled}
        />
        <Label htmlFor="gam_enabled" className="cursor-pointer">
          Enable GAM Ad Units
        </Label>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="google_manager_sdk_enabled"
          checked={values.google_manager_sdk_enabled !== false}
          onCheckedChange={checked => setFieldValue('google_manager_sdk_enabled', checked)}
          disabled={disabled}
        />
        <Label htmlFor="google_manager_sdk_enabled" className="cursor-pointer">
          Enable Google Manager SDK (for impression tracking)
        </Label>
      </div>

      {values.gam_enabled !== false && (
        <>
          <SquaresStickyFooterAd
            placement={stickyFooterPlacement ?? null}
            onPlacementChange={p => setFieldValue('sticky_footer_placement', p)}
            tenantId={tenantId}
            isSaving={disabled}
          />
          <div className="border-t pt-4 flex items-center gap-2">
            <h4 className="font-medium mb-2">Additional Ad Units (Legacy)</h4>
            <InfoIconDialog
              title="Additional Ad Units (Legacy)"
              ariaLabel="Legacy ad units information"
            >
              Contest Skin, Post Entry Banner, etc.
            </InfoIconDialog>
          </div>
          <FieldArray name="ad_units">
            {({ push, remove }) => (
              <div className="space-y-4">
                {adUnits.map((adUnit, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-start justify-between">
                      <h4 className="font-medium">Ad Unit {index + 1}</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(index)}
                        disabled={disabled}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <Label>Ad Type *</Label>
                        <Select
                          value={adUnit.type || ''}
                          onValueChange={value => {
                            const updated = [...adUnits];
                            updated[index] = { ...updated[index], type: value };
                            setFieldValue('ad_units', updated);
                          }}
                          disabled={disabled}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select ad type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="contest_skin">Contest Skin</SelectItem>
                            <SelectItem value="sticky_footer">Sticky Footer</SelectItem>
                            <SelectItem value="post_entry_banner">Post Entry Banner</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label>GAM Ad Unit ID</Label>
                        <Field
                          as={Input}
                          name={`ad_units.${index}.gam_ad_unit_id`}
                          placeholder="/12345678/ad-unit-path"
                          disabled={disabled}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Label>Sizes (comma-separated)</Label>
                        <InfoIconDialog title="Ad unit sizes" ariaLabel="Sizes information">
                          Common sizes: 1680x900, 320x50, 728x90, 300x250
                        </InfoIconDialog>
                      </div>
                      <Field
                        as={Input}
                        name={`ad_units.${index}.sizes`}
                        placeholder="1680x900, 320x50, 728x90"
                        disabled={disabled}
                      />
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => push({ type: '', sizes: [], gam_ad_unit_id: '' })}
                  disabled={disabled}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Ad Unit
                </Button>
              </div>
            )}
          </FieldArray>
        </>
      )}
    </div>
  );
}
